import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UnsavedWorkDialog } from "@/components/UnsavedWorkDialog";
import { useDraft, useLeaveWarning } from "@/lib/useDraft";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Plus, Trash2, Loader2, Undo2, Printer, CheckCircle2, Truck, AlertTriangle } from "lucide-react";

// Option 0 — bulk-spawn N NORMAL parts + N labels from a template part.
// The variant x qty table IS the default view (LH/RH is the normal case, not an edge toggle).
export function BulkSpawnDialog({ part, donor: srcDonor, lot, open, onOpenChange, onDone }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([{ variant_label: "", count: 1 }]);
  const [copyPhotos, setCopyPhotos] = useState(true);
  const [donor, setDonor] = useState("same");     // copies ALWAYS inherit the template's source
  const [cap, setCap] = useState(50);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [undoBusy, setUndoBusy] = useState(false);
  const [askDiscard, setAskDiscard] = useState(false);
  const typedRows = () => rows.filter((r) => (r.variant_label || "").trim() || (parseInt(r.count, 10) || 0) > 1);
  const draft = useDraft({
    key: `bulkspawn:${part?.id || part?._id || "none"}`,
    enabled: open && !result,
    snapshot: () => ({ rows, copyPhotos, donor }),
    restore: (d) => {
      if (!(d.rows || []).length) return;
      setRows(d.rows); setCopyPhotos(d.copyPhotos !== false); if (d.donor) setDonor(d.donor);
    },
    isWorth: (d) => (d?.rows || []).some((r) => (r.variant_label || "").trim() || (parseInt(r.count, 10) || 0) > 1),
  });
  useLeaveWarning(open && !result && typedRows().length > 0);
  const close = (force = false) => {
    if (!force && !result && typedRows().length > 0) { setAskDiscard(true); return; }
    onOpenChange(false);
  };
  const discard = () => {
    draft.clear(); setRows([{ variant_label: "", count: 1 }]);
    setAskDiscard(false); onOpenChange(false);
  };

  const hasSrcDonor = !!(srcDonor || part?.donor_id);
  const isLot = !hasSrcDonor && !!(lot || part?.capture_batch_id);   // legacy / no-VIN lot part
  const isUntied = !hasSrcDonor && !isLot;                           // no truck AND no lot (rare)
  const lotLabel = lot?.name || lot?.batch_number || "this part's lot";
  const truckLabel = srcDonor
    ? [[srcDonor.year, srcDonor.make, srcDonor.model].filter(Boolean).join(" "), srcDonor.stock_number].filter(Boolean).join(" · ")
    : "this truck";

  useEffect(() => {
    if (!open) return;
    // Copies inherit the template's source: donor part -> "same" (its truck); legacy/lot or untied
    // -> "none" (no donor). The lot tie, if any, is carried by the backend regardless.
    setResult(null);
    setDonor((srcDonor || part?.donor_id) ? "same" : "none"); setCopyPhotos(true);
    api.get("/parts/bulk-spawn-config").then(({ data }) => setCap(data.cap)).catch(() => {});
  }, [open]);

  const total = rows.reduce((s, r) => s + (parseInt(r.count, 10) || 0), 0);
  const overCap = total > cap;
  const setRow = (i, patch) => setRows((s) => s.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((s) => [...s, { variant_label: "", count: 1 }]);
  const delRow = (i) => setRows((s) => (s.length > 1 ? s.filter((_, j) => j !== i) : s));

  const submit = async () => {
    if (total <= 0) { toast.error("Enter at least one to create."); return; }
    if (overCap) { toast.error(`That's ${total} — the limit is ${cap}.`); return; }
    if (!window.confirm(`This will create ${total} new part${total > 1 ? "s" : ""} and ${total} label${total > 1 ? "s" : ""}. Continue?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/parts/${part.id || part._id}/bulk-spawn`, {
        rows: rows.filter((r) => (parseInt(r.count, 10) || 0) > 0).map((r) => ({ variant_label: r.variant_label.trim(), count: parseInt(r.count, 10) })),
        copy_photos: copyPhotos, donor,
      });
      setResult(data);
      draft.clear();
      toast.success(`${data.count} parts created — labels ${data.first_seq}…${data.last_seq}.`);
      // NOTE: do NOT call onDone() here — it triggers the parent's full-page refetch, which
      // unmounts this dialog and wipes the result panel. The template part is unchanged by a
      // spawn, so there's nothing to refresh until the dialog closes / undo runs.
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create parts."); }
    finally { setBusy(false); }
  };

  const undo = async () => {
    if (!result) return;
    const pre = await api.get(`/parts/spawn-batch/${result.spawn_batch_id}/undo-preview`).then((r) => r.data).catch(() => null);
    if (!pre) { toast.error("Could not check the batch."); return; }
    const msg = pre.blocked_count
      ? `Archive ${pre.eligible_count} of ${pre.eligible_count + pre.blocked_count}? ${pre.blocked_count} are real inventory (on a cart / sold) and will be LEFT as-is.`
      : `Archive all ${pre.eligible_count} just-created parts and retire their tags? Nothing is deleted.`;
    if (!window.confirm(msg)) return;
    setUndoBusy(true);
    try {
      const { data } = await api.post(`/parts/spawn-batch/${result.spawn_batch_id}/undo`);
      toast.success(`${data.archived_count} archived${data.blocked_count ? `, ${data.blocked_count} left (real inventory)` : ""}.`);
      onDone && onDone();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Undo failed."); }
    finally { setUndoBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg" data-testid="bulk-spawn-dialog">
          <DialogHeader>
            <DialogTitle>Duplicate “{part.name}” ×N</DialogTitle>
            <DialogDescription>Creates fully normal parts — each gets its own number, tag and {isLot ? "lot tie" : "source tie"}. Labels print as one batch on Make Tags.</DialogDescription>
          </DialogHeader>

          {!result ? (
            <div className="space-y-4">
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1.5">Variants &amp; quantity</div>
                <div className="space-y-2">
                  {rows.map((r, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`spawn-row-${i}`}>
                      <Input data-testid={`spawn-variant-${i}`} value={r.variant_label} placeholder="Variant (e.g. LH) — optional"
                        onChange={(e) => setRow(i, { variant_label: e.target.value })} className="rounded-lg h-10 flex-1" />
                      <Input data-testid={`spawn-count-${i}`} type="number" min={1} value={r.count}
                        onChange={(e) => setRow(i, { count: e.target.value })} className="rounded-lg h-10 w-20 text-center" />
                      <Button type="button" variant="ghost" size="icon" onClick={() => delRow(i)} disabled={rows.length === 1} className="h-10 w-10 text-slate-400"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" onClick={addRow} data-testid="spawn-add-variant" className="rounded-lg h-9 mt-2 border-dashed"><Plus className="w-4 h-4 mr-1.5" />Add a variant (LH / RH / trim)</Button>
              </div>

              <div className="text-sm">
                <label className="flex items-center gap-2" data-testid="spawn-copy-photos">
                  <input type="checkbox" checked={copyPhotos} onChange={(e) => setCopyPhotos(e.target.checked)} className="w-4 h-4" />
                  Copy photos (you can replace them per part later)
                </label>
              </div>

              {/* Source — copies ALWAYS inherit the template's source. Stated as a fact, no choice. */}
              <div data-testid="spawn-donor" className="space-y-2">
                <div className="text-xs font-semibold text-slate-500">Source</div>
                {hasSrcDonor ? (
                  <div data-testid="spawn-source-donor" className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    <Truck className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                    <div>All {total > 0 ? total : "N"} copies stay on <span className="font-semibold text-slate-900">{truckLabel}</span> — the same truck as this part.</div>
                  </div>
                ) : isLot ? (
                  <div data-testid="spawn-lot-note" className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    All {total > 0 ? total : "N"} copies join <span className="font-semibold text-slate-900">{lotLabel}</span> — no truck, which is correct for legacy / no-VIN parts. Each carries the lot as its source, so it stays sellable.
                  </div>
                ) : (
                  <div data-testid="spawn-untied-note" className="flex items-start gap-1.5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                    <span>This part has no truck and no lot, so every copy will have no source — they’ll show as “awaiting a source truck” until you assign one.</span>
                  </div>
                )}
              </div>

              <div className={`text-sm font-semibold ${overCap ? "text-[#B01E28]" : "text-slate-700"}`} data-testid="spawn-total">
                {overCap ? `${total} exceeds the limit of ${cap}` : `Creates ${total} part${total !== 1 ? "s" : ""} and ${total} label${total !== 1 ? "s" : ""}`}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => close()} className="rounded-lg">Cancel</Button>
                <Button onClick={submit} disabled={busy || overCap || total <= 0} data-testid="spawn-submit" className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}Create {total > 0 ? total : ""}
                </Button>
              </DialogFooter>
        <UnsavedWorkDialog open={askDiscard} onOpenChange={setAskDiscard} count={typedRows().length}
          noun="row of copies" onDiscard={discard} testid="bulkspawn-unsaved" />
            </div>
          ) : (
            <div className="space-y-4" data-testid="spawn-result">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div className="text-sm text-emerald-800">
                  <div className="font-bold">{result.count} parts created.</div>
                  Labels <span className="font-mono">{result.first_seq}…{result.last_seq}</span> are ready to print as one batch (with the usual confirm &amp; recovery).
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => navigate("/make-tags")} data-testid="spawn-print" className="rounded-lg bg-[#1F1F1F] hover:bg-black text-white"><Printer className="w-4 h-4 mr-1.5" />Print labels (Make Tags)</Button>
                <Button variant="outline" onClick={undo} disabled={undoBusy} data-testid="spawn-undo" className="rounded-lg border-[#B01E28] text-[#B01E28] hover:bg-red-50">
                  {undoBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Undo2 className="w-4 h-4 mr-1.5" />}Undo this batch
                </Button>
                <Button variant="ghost" onClick={() => close()} className="rounded-lg">Done</Button>
              </div>
              <p className="text-xs text-slate-500">Undo archives the just-created parts and retires their tags. Any that are already on a cart or sold are left untouched.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
  );
}
